// Which model answers for each AI role. Lives under the key in the
// Anthropic gear dialog, because the list of models comes from that key.
//
// The options are the catalogue the firm's own key returns, never a list
// shipped with the app: a shipped one would offer models the account cannot
// use, hide ones it can, and go stale on our release schedule rather than
// Anthropic's. Nothing here is a free-text field, for the same reason - the
// server accepts any well-formed id and a wrong one fails at drafting time.

import { useState } from "react";
import { useCurrentUser } from "../auth/CurrentUserContext";
import { isAdminRole } from "../../lib/roles";
import { Skeleton, SkeletonGroup } from "../chrome/Skeleton";
import { useAiModels, useAiModelCatalogue, useSaveAiModels } from "../../hooks/useAiModels";
import type { AiCatalogueModel } from "../../api/integrations";

// What each role is for, in the words of someone deciding. The roles
// themselves come from the server: inventing them here would let a renamed
// one render as a choice the firm never made.
const ROLE_COPY: Record<string, { label: string; hint: string }> = {
  REASONING: {
    label: "Drafting and analysis",
    hint: "Motions, cover letters, case summaries and the assistant - the work an attorney signs.",
  },
  EXTRACTION: {
    label: "Reading documents",
    hint: "Pulling names, dates and numbers out of filings, notices and identity documents.",
  },
  CLASSIFICATION: {
    label: "Sorting",
    hint: "Short decisions, such as which practice area a new intake belongs to.",
  },
  FALLBACK: {
    label: "Stand-in",
    hint: "Motion drafting only, and only when the model above is unavailable. Nothing else has a stand-in.",
  },
};

function optionLabel(model: AiCatalogueModel): string {
  if (!model.maxOutputTokens) return model.displayName;
  return `${model.displayName} - up to ${Math.round(model.maxOutputTokens / 1000)}k output`;
}

// The list answers with dated snapshots while the ids we ship are the
// undated aliases that resolve to them: claude-haiku-4-5 is served, but only
// claude-haiku-4-5-20251001 is listed. Comparing for equality alone tells
// the firm its own working default is broken.
function servedBy(value: string, catalogue: AiCatalogueModel[]): AiCatalogueModel | undefined {
  return catalogue.find((m) => m.id === value || m.id.replace(/-\d{8}$/, "") === value);
}

export function AiModelsSection() {
  const user = useCurrentUser();
  const isAdmin = isAdminRole(user?.role);
  const modelsQ = useAiModels(isAdmin);
  const catalogueQ = useAiModelCatalogue(isAdmin);
  const save = useSaveAiModels();

  const [draft, setDraft] = useState<Record<string, string>>({});

  const stored = modelsQ.data;
  const catalogue = catalogueQ.data?.models ?? [];

  // Only what actually differs from what is stored. Saving a role back to
  // its own value would rewrite the signature on the row for nothing.
  const changes: Record<string, string> = {};
  if (stored) {
    for (const [role, value] of Object.entries(draft)) {
      if (value !== stored.choices[role]) changes[role] = value;
    }
  }
  const changeCount = Object.keys(changes).length;

  function onSave() {
    save.mutate(changes, { onSuccess: () => setDraft({}) });
  }

  return (
    <section className="settings-section">
      <h3>Models</h3>
      <p className="settings-sub">
        Which model answers for each kind of work. The list is what this key can serve; leaving a
        role alone keeps the model the app ships with.
      </p>

      {modelsQ.isLoading && (
        <SkeletonGroup>
          <Skeleton width={220} />
          <Skeleton width={180} />
        </SkeletonGroup>
      )}
      {modelsQ.isError && (
        <p className="settings-error">Could not load the model settings. Refresh to retry.</p>
      )}

      {stored && (
        <>
          {stored.updatedAt && (
            <p className="settings-hint">
              Chosen {new Date(stored.updatedAt).toLocaleDateString()}
              {stored.updatedByEmail ? ` by ${stored.updatedByEmail}` : ""}
            </p>
          )}
          {/* The server's own sentence, which distinguishes a key that no
              longer decrypts from one that is missing. */}
          {catalogueQ.isError && (
            <p className="settings-warn">{(catalogueQ.error as Error).message}</p>
          )}
          {/* "not configured" is not news here: the key panel above says it,
              and Anthropic was never asked. */}
          {catalogueQ.data?.errorCode && catalogueQ.data.errorCode !== "not_configured" && (
            <p className="settings-warn">
              Anthropic did not return a model list ({catalogueQ.data.errorCode}).
              {catalogueQ.data.reason ? ` ${catalogueQ.data.reason}` : ""}
            </p>
          )}

          <div className="settings-grid">
            {/* The set of roles is the server's; the reading order is ours,
                so the work an attorney signs stays at the top. A role we
                have no copy for still renders, under its own name. */}
            {[
              ...Object.keys(ROLE_COPY).filter((r) => r in stored.defaults),
              ...Object.keys(stored.defaults).filter((r) => !(r in ROLE_COPY)),
            ].map((role) => {
              const copy = ROLE_COPY[role] ?? { label: role, hint: "" };
              const onFile = stored.choices[role] ?? "";
              const value = draft[role] ?? onFile;
              const twin = servedBy(onFile, catalogue);
              const listed = catalogue.some((m) => m.id === onFile);
              const shipped = stored.defaults[role];
              return (
                <label className="settings-field" key={role}>
                  <span className="settings-label">{copy.label}</span>
                  <select
                    className="cc-field"
                    value={value}
                    disabled={save.isPending}
                    onChange={(e) => setDraft({ ...draft, [role]: e.target.value })}
                  >
                    {/* Keyed on what is stored, not on the current pick, so
                        an id the list does not carry stays reachable after
                        the admin tries something else and changes their
                        mind. */}
                    {!listed && onFile && (
                      <option value={onFile}>
                        {twin ? `${twin.displayName} (${onFile})` : onFile}
                      </option>
                    )}
                    {catalogue.map((m) => (
                      <option value={m.id} key={m.id}>
                        {optionLabel(m)}
                      </option>
                    ))}
                  </select>
                  {copy.hint && <span className="settings-hint">{copy.hint}</span>}
                  {catalogue.length > 0 && !servedBy(value, catalogue) && (
                    <span className="settings-warn">
                      This key does not serve {value}. Every call for this role fails until another
                      is chosen.
                    </span>
                  )}
                  {value !== shipped && (
                    <span className="settings-hint">Shipped default: {shipped}</span>
                  )}
                </label>
              );
            })}
          </div>

          <div className="settings-actions">
            <button
              type="button"
              className="settings-save-btn"
              disabled={changeCount === 0 || save.isPending}
              onClick={onSave}
            >
              {save.isPending ? "Saving..." : "Save models"}
            </button>
          </div>

          {save.isError && <p className="settings-error">{(save.error as Error).message}</p>}
          {save.isSuccess && changeCount === 0 && (
            <p className="settings-ok" role="status">
              Models saved.
            </p>
          )}
        </>
      )}
    </section>
  );
}
